import { normalizeWidgetTheme, APIURL, apiGet, cancelSelectEvent, getCSSStyles, getNoDataHTML, getWidgetDataURI, toggleWidgetTheme, isHeaderPinned, getHeaderPinStateClass, getHeaderPinButtonHTML, setupHeaderPin } from '../../helpers';
import { WidgetConfig, WidgetEvent, WidgetEventHandler, WidgetEventType } from '../../types';

interface NasdaqTickerItem {
  symbol: string;
  changePct: number;
}

interface NasdaqConstituentsApiRow {
  symbol: string;
  tvId?: string;
  change_percentage: number;
}

interface NasdaqConstituentsApiResponse {
  data?: NasdaqConstituentsApiRow[];
}

interface NasdaqWidgetConfig extends WidgetConfig {
  /** Override widget title in header */
  title?: string;
}

export class TraderverseStockNASDAQ {
  private container: HTMLElement;
  private config: NasdaqWidgetConfig;
  private shadowRoot: ShadowRoot;
  private eventHandlers: Map<WidgetEventType, WidgetEventHandler[]> = new Map();
  private tickerItems: NasdaqTickerItem[] = [];

  constructor(config: WidgetConfig) {
    this.config = { ...this.getDefaultConfig(), ...config } as NasdaqWidgetConfig;
    this.config.theme = normalizeWidgetTheme(this.config.theme);
    this.container = this.resolveContainer(config.container);

    if ((this.container as any).shadowRoot) {
      this.shadowRoot = (this.container as any).shadowRoot;
    } else {
      this.shadowRoot = this.container.attachShadow({ mode: 'closed' });
    }

    this.init();
  }

  private getDefaultConfig(): Partial<NasdaqWidgetConfig> {
    return {
      headerPinned: true,
      symbol: 'AAPL',
      theme: 'black',
      width: '100%',
      height: 'auto',
      autosize: true,
      title: 'NASDAQ',
    };
  }

  private resolveContainer(container: string | HTMLElement): HTMLElement {
    if (typeof container === 'string') {
      const el = document.getElementById(container) || document.querySelector(container);
      if (!el) throw new Error(`Container not found: ${container}`);
      return el as HTMLElement;
    }
    return container;
  }

  private async init(): Promise<void> {
    await this.loadTickerItems();
    this.createWidget();
    const tickerOuter = this.shadowRoot.querySelector(
    '.nas-ticker-outer'
  ) as HTMLElement;

  if (tickerOuter) {
    if (this.tickerItems.length > 0) {
      tickerOuter.setAttribute('data-testing-id', 'nasdaq-ticker');
    } else {
      tickerOuter.removeAttribute('data-testing-id');
    }
  }
    this.setupEventListeners();
    await this.attachStyles();
    this.emit('ready', {});
    cancelSelectEvent(this.shadowRoot);
    this.config.onReady?.();
  }

  private fetchSymbols(): Array<{ value: string; label: string }> {
    return [
      { label: 'AAPL', value: 'AAPL' }, { label: 'MSFT', value: 'MSFT' },
      { label: 'GOOGL', value: 'GOOGL' }, { label: 'AMZN', value: 'AMZN' },
      { label: 'NVDA', value: 'NVDA' }, { label: 'META', value: 'META' },
      { label: 'TSLA', value: 'TSLA' }, { label: 'AVGO', value: 'AVGO' },
      { label: 'PEP', value: 'PEP' }, { label: 'COST', value: 'COST' }
    ];
  }

  private setupEventListeners(): void {
    setupHeaderPin(this.shadowRoot, this.config);
    const symbolDropdown = this.shadowRoot.querySelector('.symbol-dropdown') as HTMLSelectElement | null;
    symbolDropdown?.addEventListener('change', (event) => {
      const symbol = (event.target as HTMLSelectElement).value;
      this.config.symbol = symbol;
      this.config.onSymbolChange?.(symbol);
      this.emit('symbolChange', { symbol });
    });
    if (symbolDropdown && this.config.symbol) symbolDropdown.value = this.config.symbol;
  }

  private async attachStyles(): Promise<void> {
    try {
      const styleEl = await getCSSStyles();
      this.shadowRoot.appendChild(styleEl);
    } catch (err) {
      console.error('Failed to load CSS file:', err);
    }
  }

  private async loadTickerItems(): Promise<void> {
    try {
      const res = await apiGet({
        url: `${APIURL}/v3/discover/nasdaq-constituents-price-change`,
      });
      if (!res.ok) {
        console.error('NASDAQ constituents request failed:', res.status, res.statusText);
        this.tickerItems = [];
        return;
      }
      const json = (await res.json()) as NasdaqConstituentsApiResponse;
      const rows = Array.isArray(json.data) ? json.data : [];
      this.tickerItems = rows.map((r) => ({
        symbol: String(r.symbol ?? '').trim(),
        changePct:
          typeof r.change_percentage === 'number' && Number.isFinite(r.change_percentage)
            ? r.change_percentage
            : 0,
      }));
    } catch (e) {
      console.error('Failed to fetch NASDAQ constituents price change:', e);
      this.tickerItems = [];
    }
  }

  private formatPctSigned(n: number): string {
    const sign = n > 0 ? '+' : '';
    return `${sign}${n.toFixed(2)}%`;
  }

  private pillHtml(items: NasdaqTickerItem[]): string {
    const valid = items.filter((i) => i.symbol.length > 0);
    return valid
      .map((i) => {
        const up = i.changePct >= 0;
        const tri = up ? '▲' : '▼';
        const cls = up ? 'is-up' : 'is-down';
        return `<div class="nas-ticker-pill" role="listitem">
        <span class="nas-pill-sym">${i.symbol}</span>
        <span class="nas-pill-tri ${cls}" aria-hidden="true">${tri}</span>
        <span class="nas-pill-pct ${cls}">${this.formatPctSigned(i.changePct)}</span>
      </div>`;
      })
      .join('');
  }

  private createWidget(): void {
    const themeClass =
      this.config.theme === 'white' ? 'whiteMode' : this.config.theme === 'black' ? 'blackMode' : this.config.theme === 'default-lite' ? 'defaultLightMode' : 'defaultDarkMode';
    const useFillLayout = Boolean(this.config.height && this.config.height !== 'auto');
    const fillClass = useFillLayout ? ' nasdaq-widget--fill' : '';
    const title = this.config.title || 'NASDAQ';
    const symbolOptions = this.fetchSymbols()
      .map((s) => `<option value="${s.value}" ${s.value === this.config.symbol ? 'selected' : ''}>${s.label}</option>`)
      .join('');
    const pills = this.pillHtml(this.tickerItems);
    const tickerBody =
      pills.length > 0
        ? `<div class="nas-ticker-track">${pills}${pills}</div>`
        : `<div class="nas-ticker-track" style="animation:none;display:flex;align-items:center;justify-content:center;padding:12px;">${getNoDataHTML()}</div>`;

    const widgetHtml = `
      <div style="width: ${typeof this.config.width === 'number' ? this.config.width + 'px' : this.config.width};${this.config.height === 'auto' ? '' : `height: ${typeof this.config.height === 'number' ? this.config.height + 'px' : this.config.height};`}" class="widgetBox nasdaq-widget${fillClass} ${themeClass}" data-theme="${this.config.theme}">
        <div class="hide headTop">
          <h4 class="title stock">${this.getTitleIconSvg()} ${title}</h4>
          <div class="actionCol">${getHeaderPinButtonHTML(this.config)}<i class="pi pi-ellipsis-v"></i></div>
        </div>
        <div class="inBody">
          <div class="optionsRow">     
          </div>
          <div class="nas-ticker-outer" aria-label="Moving tickers">
            ${tickerBody}
          </div>
        </div>
      </div>`;

    this.shadowRoot.innerHTML = widgetHtml;
  }

  public on(eventType: WidgetEventType, handler: WidgetEventHandler): void {
    if (!this.eventHandlers.has(eventType)) this.eventHandlers.set(eventType, []);
    this.eventHandlers.get(eventType)!.push(handler);
  }

  public off(eventType: WidgetEventType, handler: WidgetEventHandler): void {
    const list = this.eventHandlers.get(eventType);
    if (list) this.eventHandlers.set(eventType, list.filter((h) => h !== handler));
  }

  public async getWidgetImage(): Promise<string> {
    const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    return await getWidgetDataURI(widget);
  }

  public setTheme(theme: 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite'): void {
    if (!this.shadowRoot) return;
    this.config.theme = toggleWidgetTheme(this.shadowRoot, theme) as 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite';
  }

  public async refresh(): Promise<void> {
    await this.loadTickerItems();
    this.createWidget();
    this.setupEventListeners();
    void this.attachStyles();
  }

  public destroy(): void {
    this.eventHandlers.clear();
    this.shadowRoot.innerHTML = '';
  }

  private emit(type: WidgetEventType, data: any): void {
    const handlers = this.eventHandlers.get(type);
    if (handlers) {
      const evt: WidgetEvent = { type, data };
      handlers.forEach((h) => h(evt));
    }
  }

  private getTitleIconSvg(): string {
    return `<svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M8.59998 23.625C8.08748 23.625 7.66248 23.2 7.66248 22.6875V20.1C7.66248 19.5875 8.08748 19.1625 8.59998 19.1625C9.11248 19.1625 9.53748 19.5875 9.53748 20.1V22.6875C9.53748 23.2125 9.11248 23.625 8.59998 23.625Z" fill="currentColor"/> <path d="M15 23.625C14.4875 23.625 14.0625 23.2 14.0625 22.6875V17.5C14.0625 16.9875 14.4875 16.5625 15 16.5625C15.5125 16.5625 15.9375 16.9875 15.9375 17.5V22.6875C15.9375 23.2125 15.5125 23.625 15 23.625Z" fill="currentColor"/> <path d="M21.4 23.625C20.8875 23.625 20.4625 23.2 20.4625 22.6875V14.9125C20.4625 14.4 20.8875 13.975 21.4 13.975C21.9125 13.975 22.3375 14.4 22.3375 14.9125V22.6875C22.3375 23.2125 21.925 23.625 21.4 23.625Z" fill="currentColor"/> <path d="M8.59997 16.475C8.17497 16.475 7.79997 16.1875 7.68747 15.7625C7.56247 15.2625 7.86247 14.75 8.37497 14.625C12.975 13.475 17.025 10.9625 20.1125 7.37502L20.6875 6.70002C21.025 6.31252 21.6125 6.26252 22.0125 6.60002C22.4 6.93752 22.45 7.52502 22.1125 7.92502L21.5375 8.60002C18.2 12.5 13.8 15.2125 8.82497 16.45C8.74997 16.475 8.67497 16.475 8.59997 16.475Z" fill="currentColor"/> <path d="M21.4001 11.9C20.8876 11.9 20.4626 11.475 20.4626 10.9625V8.25H17.7375C17.225 8.25 16.8 7.825 16.8 7.3125C16.8 6.8 17.225 6.375 17.7375 6.375H21.4001C21.9126 6.375 22.3376 6.8 22.3376 7.3125V10.975C22.3376 11.4875 21.9251 11.9 21.4001 11.9Z" fill="currentColor"/> <path d="M18.75 28.4375H11.25C4.4625 28.4375 1.5625 25.5375 1.5625 18.75V11.25C1.5625 4.4625 4.4625 1.5625 11.25 1.5625H18.75C25.5375 1.5625 28.4375 4.4625 28.4375 11.25V18.75C28.4375 25.5375 25.5375 28.4375 18.75 28.4375ZM11.25 3.4375C5.4875 3.4375 3.4375 5.4875 3.4375 11.25V18.75C3.4375 24.5125 5.4875 26.5625 11.25 26.5625H18.75C24.5125 26.5625 26.5625 24.5125 26.5625 18.75V11.25C26.5625 5.4875 24.5125 3.4375 18.75 3.4375H11.25Z" fill="currentColor"/> </svg>`;
  }
}

declare global {
  interface Window {
    TraderverseStockNASDAQ?: typeof TraderverseStockNASDAQ;
  }
}

if (typeof window !== 'undefined') {
  window.TraderverseStockNASDAQ = TraderverseStockNASDAQ;
}
